import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import CashManagementDashboard from './CashManagementDashboard';
import BudgetPlanningDashboard from './BudgetPlanningDashboard';
import CashFlowForecastDashboard from './CashFlowForecastDashboard';
import { supabase } from '../../lib/supabase';
import { useTeachingMode } from '../../contexts/TeachingModeContext';
import EnhancedTeachingAids from '../EnhancedTeachingAids';

const FinancialManagementView = () => {
  const [activeTab, setActiveTab] = useState('cash-management');
  const [pendingTransactions, setPendingTransactions] = useState([]);
  const [lastUpdated, setLastUpdated] = useState('');
  const [financialData, setFinancialData] = useState({
    cashBalance: 0,
    netIncome: 0
  });
  const [loading, setLoading] = useState(true);
  const { showTeachingAids } = useTeachingMode();
  
  // Fetch financial data from Supabase
  useEffect(() => {
    fetchFinancialData();
  }, []);

  const fetchFinancialData = async () => {
    try {
      setLoading(true);
      
      // Fetch financial journal entries
      const { data: journalData, error: journalError } = await supabase
        .from('financial_journal_entries')
        .select('*');

      if (journalError) throw journalError;

      // Fetch chart of accounts
      const { data: accountsData, error: accountsError } = await supabase
        .from('chart_of_accounts')
        .select('*');

      if (accountsError) throw accountsError;

      // Calculate cash balance from cash accounts
      const cashAccounts = accountsData?.filter(acc => acc.account_type === 'Asset' && acc.account_name?.toLowerCase().includes('cash')) || [];
      const cashBalance = cashAccounts.reduce((total, account) => total + (parseFloat(account.balance) || 0), 0);

      // Calculate net income from revenue and expense accounts
      const revenueAccounts = accountsData?.filter(acc => acc.account_type === 'Revenue') || [];
      const expenseAccounts = accountsData?.filter(acc => acc.account_type === 'Expense') || [];
      
      const totalRevenue = revenueAccounts.reduce((total, account) => total + (parseFloat(account.balance) || 0), 0);
      const totalExpenses = expenseAccounts.reduce((total, account) => total + (parseFloat(account.balance) || 0), 0);
      const netIncome = totalRevenue - totalExpenses;

      // Get pending transactions
      const pending = journalData?.filter(entry => entry.status === 'draft' || entry.status === 'pending') || [];

      setFinancialData({
        cashBalance,
        netIncome
      });
      setPendingTransactions(pending);
      
      // Set last updated timestamp
      const now = new Date();
      setLastUpdated(now.toLocaleString('zh-HK'));
    } catch (error) {
      console.error('Error fetching financial data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Format financial data
  const cashBalanceFormatted = financialData.cashBalance.toLocaleString('zh-HK', { style: 'currency', currency: 'HKD' });
  const netIncomeFormatted = financialData.netIncome.toLocaleString('zh-HK', { style: 'currency', currency: 'HKD' });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading financial management system...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">財務管理系統</h1>
          <p className="text-gray-500 text-sm mt-1">最後更新: {lastUpdated}</p>
        </div>
        
        <div className="flex flex-wrap gap-4">
          <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-100">
            <div className="text-sm text-gray-500">現金餘額</div>
            <div className="text-xl font-semibold text-gray-800">{cashBalanceFormatted}</div>
          </div>
          
          <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-100">
            <div className="text-sm text-gray-500">本月淨收入</div>
            <div className="text-xl font-semibold text-gray-800">{netIncomeFormatted}</div>
          </div>
          
          <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-100">
            <div className="text-sm text-gray-500">待處理交易</div>
            <div className="flex items-center">
              <span className="text-xl font-semibold text-gray-800 mr-2">{pendingTransactions.length}</span>
              {pendingTransactions.length > 0 && (
                <Badge variant="destructive" className="h-6">需要處理</Badge>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Financial system hero image */}
      <div className="relative w-full h-48 mb-8 rounded-xl overflow-hidden">
        <img 
          src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2000&q=80" 
          alt="Financial Management System" 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/70 to-transparent flex items-center p-8">
          <div className="text-white max-w-md">
            <h2 className="text-2xl font-bold mb-2">財務管理儀表板</h2>
            <p className="text-white/90">全面的財務管理工具，幫助您監控現金流、規劃預算和預測未來財務狀況。</p>
          </div>
        </div>
      </div>

      <Tabs
        defaultValue="cash-management"
        value={activeTab}
        onValueChange={setActiveTab}
        className="w-full"
      >
        <TabsList className="grid w-full grid-cols-3 mb-8">
          <TabsTrigger value="cash-management">現金管理</TabsTrigger>
          <TabsTrigger value="budget-planning">預算規劃</TabsTrigger>
          <TabsTrigger value="cash-flow-forecast">現金流預測</TabsTrigger>
        </TabsList>

        <TabsContent value="cash-management">
          <Card className="p-0 overflow-hidden">
            <CashManagementDashboard />
          </Card>
        </TabsContent>

        <TabsContent value="budget-planning">
          <Card className="p-0 overflow-hidden">
            <BudgetPlanningDashboard />
          </Card>
        </TabsContent>

        <TabsContent value="cash-flow-forecast">
          <Card className="p-0 overflow-hidden">
            <CashFlowForecastDashboard />
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Teaching Aids Panel */}
      {showTeachingAids && (
        <EnhancedTeachingAids 
          module="moneyCycle" 
          isVisible={true}
        />
      )}
    </div>
  );
};

export default FinancialManagementView;
